import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useAuthenticatedMediaUrl } from "@/hooks/useAuthenticatedMedia";
import { sizedCdnImage, imageRequestWidth } from "@/lib/cdnImage";

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  imgClassName?: string;
  imgStyle?: React.CSSProperties;
  thumbnailSrc?: string;
  placeholderColor?: string;
  aspectRatio?: "square" | "video" | "portrait" | "auto";
  onLoad?: () => void;
  onError?: () => void;
  /** Explicit intrinsic width for the img element — prevents CLS when the
   *  container has no fixed size. Forwarded directly to the <img> attribute. */
  width?: number | string;
  /** Explicit intrinsic height for the img element — prevents CLS when the
   *  container has no fixed size. Forwarded directly to the <img> attribute. */
  height?: number | string;
  /** Opt in only for known photo surfaces, never video/opaque media. */
  cdnSize?: boolean;
  /** Extra HTML attributes forwarded verbatim to the wrapper div
   *  (e.g. data-testid). */
  [key: `data-${string}`]: unknown;
}

/**
 * Lazy loading image component with placeholder support
 * Uses Intersection Observer to only load images when they enter the viewport
 */
export function LazyImage(props: LazyImageProps) {
  // A carousel source change gets independent loading/error/fallback state.
  return <LazyImageSource key={props.src} {...props} />;
}

function LazyImageSource({
  src,
  alt,
  className,
  style,
  imgClassName,
  imgStyle,
  thumbnailSrc,
  placeholderColor = "bg-muted",
  aspectRatio = "auto",
  onLoad,
  onError,
  width,
  height,
  cdnSize = false,
  ...rest
}: LazyImageProps) {
  const resolvedSrc = useAuthenticatedMediaUrl(src) ?? src;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [requestWidth, setRequestWidth] = useState(0);
  const [failedVariant, setFailedVariant] = useState<string | null>(null);
  const imgRef = useRef<HTMLDivElement>(null);
  const variant = cdnSize && requestWidth
    ? sizedCdnImage(resolvedSrc, requestWidth, undefined, true)
    : resolvedSrc;
  const imageSrc = failedVariant === resolvedSrc ? resolvedSrc : variant;

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setFailedVariant(null);
  }, [resolvedSrc]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;
    const measure = () => setRequestWidth(imageRequestWidth(
      element.clientWidth, window.devicePixelRatio,
    ));
    if (typeof IntersectionObserver === "undefined") {
      measure();
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            measure();
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: "50px", // Start loading 50px before entering viewport
        threshold: 0.01,
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!cdnSize || !isInView || !imgRef.current ||
        typeof ResizeObserver === "undefined") return;
    const element = imgRef.current;
    const observer = new ResizeObserver(() => setRequestWidth(imageRequestWidth(
      element.clientWidth, window.devicePixelRatio,
    )));
    observer.observe(element);
    return () => observer.disconnect();
  }, [cdnSize, isInView]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    if (imageSrc !== resolvedSrc) {
      setFailedVariant(resolvedSrc);
      setIsLoaded(false);
      return;
    }
    setHasError(true);
    onError?.();
  };

  const aspectClasses = {
    square: "aspect-square",
    video: "aspect-video",
    portrait: "aspect-[3/4]",
    auto: "",
  };

  return (
    <div
      ref={imgRef}
      className={cn(
        "relative overflow-hidden",
        aspectClasses[aspectRatio],
        placeholderColor,
        className
      )}
      style={style}
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
    >
      {/* Thumbnail/placeholder (low quality, loads immediately) */}
      {thumbnailSrc && isInView && !cdnSize && !isLoaded && (
        <img
          src={thumbnailSrc}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-sm scale-105"
          aria-hidden="true"
        />
      )}

      {/* Main image (loads when in view) */}
      {isInView && !hasError && (
        <img
          key={imageSrc}
          src={imageSrc}
          alt={alt}
          width={width}
          height={height}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
            imgClassName
          )}
          style={imgStyle}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
        />
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <span className="text-muted-foreground text-sm">Failed to load</span>
        </div>
      )}

      {/* Loading shimmer */}
      {!isLoaded && !hasError && (!thumbnailSrc || cdnSize) && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-muted via-muted/50 to-muted" />
      )}
    </div>
  );
}

/**
 * Simple image component with built-in lazy loading
 * For cases where you don't need thumbnail support
 */
export function SimpleImage({
  src,
  alt,
  className,
  ...props
}: React.ImgHTMLAttributes<HTMLImageElement>) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className={cn("relative overflow-hidden bg-muted", className)}>
      <img
        src={src}
        alt={alt}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          isLoaded ? "opacity-100" : "opacity-0"
        )}
        onLoad={() => setIsLoaded(true)}
        loading="lazy"
        {...props}
      />
      {!isLoaded && <div className="absolute inset-0 animate-pulse bg-muted" />}
    </div>
  );
}

export default LazyImage;
